import { h } from 'preact';
import { useState, useEffect } from 'preact/compat';
import axios from "../../utils/axios-interceptor";
import {captureError} from "../../utils/sentry";
import {sanitizeHtml} from "../../utils/sanitize";
import OAuthSuccess from "../oauth-success";
import OAuthError from "../oauth-error";

/**
 * CustomPage Component
 *
 * CODE REUSE: This component renders pages from both PaymentPageLayout and SystemSubdomain.
 * Both collections use identical page structure with 'pageType' field.
 * SystemSubdomain is like PaymentPageLayout but without payment gateways or transaction processing.
 *
 * Fetches and renders dynamic content from the database based on the URL slug.
 * Supports multiple page types:
 * - 'html' or undefined: Static HTML content (default)
 * - 'clover-oauth-success': Renders Clover OAuth success component
 * - 'clover-oauth-error': Renders Clover OAuth error component
 *
 * Used for custom pages like terms-of-service, privacy-policy, oauth flows, etc.
 *
 * @param {string} slug - The URL path slug for the page
 * @param {boolean} isSystemSubdomain - If true, fetch from /system-subdomain/page endpoint
 */
const CustomPage = ({ slug, isSystemSubdomain = false }) => {
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get the API URL from dns-resolver
                let dnsResolverUrl = '';
                if (window.location.href.indexOf('localhost') > -1 && localStorage.getItem('useLocalhostApi') === 'true') {
                    dnsResolverUrl = 'http://localhost:5001/api/dns-resolver/resolve';
                } else {
                    dnsResolverUrl = 'https://dns.ourcheckout.net/api/dns-resolver/resolve';
                }

                const dnsResponse = await axios.get(dnsResolverUrl);
                let apiUrl = dnsResponse.data.api;

                if (window.location.href.indexOf('localhost') > -1 || window.location.href.indexOf('portalsfront.ngrok.app') > -1) {
                    apiUrl = `${process.env.PREACT_APP_LOCALHOST_API}api/`;
                }

                // Fetch the page content from appropriate endpoint
                const endpoint = isSystemSubdomain ? 'system-subdomain/page' : 'portal/page';
                const response = await axios.get(`${apiUrl}${endpoint}`, {
                    params: { url: window.location.hostname, slug: slug }
                });

                // Page can have content (HTML) or pageType (component-based)
                // CODE REUSE: Both PaymentPageLayout and SystemSubdomain use 'pageType' field
                if (response.data && (response.data.content || response.data.pageType)) {
                    setPageData(response.data);

                    // Update document title
                    if (response.data.title) {
                        document.title = response.data.title;
                    }

                    // Update favicon if portal/subdomain has logo
                    const logoSource = response.data.subdomain || response.data.portal;
                    if (logoSource && logoSource.logo) {
                        let link = document.querySelector("link[rel~='icon']");
                        if (link) {
                            link.href = logoSource.logo;
                        }
                    }
                } else {
                    setError('Page not found');
                }
            } catch (err) {
                console.error('Error fetching page:', err);
                captureError(err, { type: 'custom-page-fetch-error' });
                setError(err.response?.data?.message || 'Page not found');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchPage();
        }
    }, [slug]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>
                    <h1>Page Not Found</h1>
                    <p>The page you are looking for does not exist.</p>
                </div>
            </div>
        );
    }

    // Handle component-based page types
    // CODE REUSE: Both PaymentPageLayout and SystemSubdomain use 'pageType' field
    const pageType = pageData.pageType || 'html';

    if (pageType === 'clover-oauth-success') {
        // Extract query params for Clover OAuth success page
        const urlParams = new URLSearchParams(window.location.search);
        const oauthProps = {
            merchant_id: urlParams.get('merchant_id'),
            app_config_id: urlParams.get('app_config_id'),
            manager_name: urlParams.get('manager_name'),
            manager_email: urlParams.get('manager_email'),
            manager_mobile: urlParams.get('manager_mobile'),
            // Allow page config to override defaults
            ...pageData.config
        };
        return <OAuthSuccess {...oauthProps} />;
    }

    if (pageType === 'clover-oauth-error') {
        // Extract query params for Clover OAuth error page
        const urlParams = new URLSearchParams(window.location.search);
        const errorProps = {
            message: urlParams.get('message'),
            // Allow page config to override defaults
            ...pageData.config
        };
        return <OAuthError {...errorProps} />;
    }

    // Default: render HTML content
    return (
        <div style={styles.container}>
            <div
                style={styles.content}
                dangerouslySetInnerHTML={sanitizeHtml(pageData.content)}
            />
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        padding: '40px 20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    content: {
        maxWidth: '800px',
        margin: '0 auto',
        color: '#333',
        lineHeight: '1.6'
    },
    loading: {
        textAlign: 'center',
        padding: '100px 20px',
        fontSize: '18px',
        color: '#666'
    },
    error: {
        textAlign: 'center',
        padding: '100px 20px',
        color: '#666'
    }
};

export default CustomPage;
